import React, { useState, useEffect } from 'react';
import { useAuth, AuthProvider } from './AuthContext';
import Login from './Login';
import Register from './Register';
import { PostgreSQLManager } from './components/PostgreSQLComponents';
import './App.css';

const AppContent = () => {
    const { user, loading, logout, isAuthenticated, token } = useAuth();
    const [showLogin, setShowLogin] = useState(true);
    const [activeNav, setActiveNav] = useState('Готовое облако 1C');
    const [accountTime, setAccountTime] = useState(0);
    
    const [showLaunchModal, setShowLaunchModal] = useState(false);
    const [isLaunching, setIsLaunching] = useState(false);
    const [launchStatus, setLaunchStatus] = useState('');
    const [launchConfig, setLaunchConfig] = useState({
        description: '', 
        password: '', 
        sshKey: '',
        dns: '8.8.8.8', 
        cores: 2, 
        memory: 4096, 
        disk: 20, 
        startOnCreate: true,
    });
    
    const [lxcContainers, setLxcContainers] = useState([]);
    const [isLoadingContainers, setIsLoadingContainers] = useState(false);
    const [databases, setDatabases] = useState([]);

    const [infoBases, setInfoBases] = useState([]);
    const [isLoadingInfoBases, setIsLoadingInfoBases] = useState(false);
    const [showInfobaseModal, setShowInfobaseModal] = useState(false);
    const [isCreatingInfobase, setIsCreatingInfobase] = useState(false);
    const [infobaseConfig, setInfobaseConfig] = useState({
        name: '', description: '', container_vmid: '', locale: 'ru_RU'
    });
    
    const [showGUIModal, setShowGUIModal] = useState(false);
    const [guiVmid, setGuiVmid] = useState(null);
    const [guiUrl, setGuiUrl] = useState(null);
    const [isGUILoading, setIsGUILoading] = useState(false);
    const [guiError, setGuiError] = useState(null);
    const [showGUIStartModal, setShowGUIStartModal] = useState(false);
    
    const [guiStartConfig, setGuiStartConfig] = useState({
        resolution: '1280x1024',
        infobase_name: ''
    });
    
    const [availableInfobases, setAvailableInfobases] = useState([]);
    const [selectedInfobase, setSelectedInfobase] = useState(null);
    
    const API_BASE_URL = 'https://app.1c-lxc.ru'; 
    
    const generateRandomPassword = () => {
        const length = 12;
        const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
        let password = "";
        for (let i = 0; i < length; i++) {
            password += charset.charAt(Math.floor(Math.random() * charset.length));
        }
        return password;
    };
    
    useEffect(() => {
        if (showLaunchModal) {
            setLaunchConfig(prev => ({ ...prev, password: generateRandomPassword() }));
        }
    }, [showLaunchModal]);
    
    useEffect(() => {
        if (!isAuthenticated) return;
        const timer = setInterval(() => { setAccountTime(prev => prev + 1); }, 1000);
        return () => clearInterval(timer);
    }, [isAuthenticated]);
    
    useEffect(() => {
        if (isAuthenticated && (activeNav === 'Сервера 1C' || activeNav === 'Базы данных' || activeNav === 'Информационные базы')) {
            fetchLxcContainers();
        }
    }, [isAuthenticated, activeNav]);

    useEffect(() => {
        if (isAuthenticated && activeNav === 'Информационные базы') {
            if (infoBases.length === 0) {
                fetchInfoBases();
            }
        }
    }, [isAuthenticated, activeNav]);
    
    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };
    
    const fetchDatabases = async () => {
        if (!token) return [];
        try {
            const response = await fetch(`${API_BASE_URL}/api/postgres/databases`, { headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` } });
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.databases) { setDatabases(data.databases); return data.databases; }
            }
        } catch (error) { console.error('Ошибка при загрузке баз данных:', error); }
        return [];
    };
    
    const getContainerDatabases = async (vmid, allDatabases = null) => {
        if (allDatabases) return allDatabases.filter(db => db.container_id === vmid || db.container_vmid === vmid);
        if (!token) return [];
        try {
            const response = await fetch(`${API_BASE_URL}/api/postgres/containers/${vmid}/databases`, { headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` } });
            if (response.status === 401) { logout(); return []; }
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.databases && Array.isArray(data.databases)) return data.databases;
                else if (Array.isArray(data)) return data;
                else if (data.data && Array.isArray(data.data)) return data.data;
                else return [];
            }
            return [];
        } catch (error) { console.error(`Ошибка при загрузке БД для контейнера ${vmid}:`, error); return []; }
    };

    const fetchInfoBases = async () => {
        if (!token) return [];
        setIsLoadingInfoBases(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/infobases`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (response.ok) {
                const data = await response.json();
                setInfoBases(data);
                return data;
            }
        } catch (error) { console.error('Ошибка загрузки ИБ:', error); }
        finally { setIsLoadingInfoBases(false); }
        return [];
    };

    const fetchLxcContainers = async () => {
        if (!token) return;
        setIsLoadingContainers(true);
        try {
            const [allDatabases, allInfoBases] = await Promise.all([
                fetchDatabases(),
                fetchInfoBases()
            ]);
            
            const response = await fetch(`${API_BASE_URL}/api/lxc/containers`, { 
                headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` } 
            });
            
            if (response.status === 401) { logout(); return; }
            if (response.ok) {
                const data = await response.json();
                let containers = [];
                if (data.success && data.containers) containers = data.containers;
                else if (Array.isArray(data)) containers = data;
                else if (data.data && Array.isArray(data.data)) containers = data.data;
                else { setIsLoadingContainers(false); return; }
                
                const containersWithDatabases = await Promise.all(
                    containers.map(async (container) => {
                        let dbs = [];
                        const vmid = container.vmid || container.id;
                        
                        if (allDatabases && allDatabases.length > 0) {
                            dbs = allDatabases.filter(db => 
                                db.container_id === vmid || 
                                db.container_vmid === vmid || 
                                db.container_name === container.name
                            );
                        }
                        if (dbs.length === 0) dbs = await getContainerDatabases(vmid);
                        
                        let containerInfoBases = [];
                        if (allInfoBases && allInfoBases.length > 0) {
                            containerInfoBases = allInfoBases.filter(ib => 
                                String(ib.container_vmid) === String(vmid)
                            );
                        }
                        
                        return {
                            vmid: vmid, 
                            name: container.name || `CT-${vmid}`,
                            status: container.status === 'running' ? 'running' : 'stopped',
                            ip: container.ip || 'Не настроен', 
                            memory: container.memory || 1024,
                            cores: container.cores || container.cpus || 1, 
                            disk: container.disk || 6,
                            maxmem: container.maxmem || container.memory || 1024, 
                            maxdisk: container.maxdisk || container.disk || 6,
                            databases_count: dbs.length, 
                            databases: dbs,
                            infobases: containerInfoBases,
                            infobases_count: containerInfoBases.length,
                            apache_info: container.apache_info || { service_status: 'not_installed', is_installed: false, version: null },
                            onec_info: container.onec_info || { 
                                version: null, 
                                service_status: container.status === 'running' ? 'not_installed' : 'container_stopped', 
                                is_installed: false, 
                                available_versions: [], 
                                cluster_created: false, 
                                cluster_uuid: null 
                            }
                        };
                    })
                );
                setLxcContainers(containersWithDatabases);
            }
        } catch (error) { 
            console.error('Ошибка при загрузке контейнеров из FastAPI:', error); 
        } finally { 
            setIsLoadingContainers(false); 
        }
    };

    const handleCreateInfobase = async (e) => {
        e.preventDefault();
        if (!token || !infobaseConfig.container_vmid) return;
        setIsCreatingInfobase(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/infobases`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(infobaseConfig),
            });
            const data = await response.json();
            if (response.ok && data.success) {
                alert(`✅ ${data.message}`);
                setShowInfobaseModal(false);
                setInfobaseConfig({ name: '', description: '', container_vmid: '', locale: 'ru_RU' });
                fetchInfoBases();
                fetchLxcContainers();
            } else {
                alert(`❌ Ошибка: ${data.detail || data.message || 'Неизвестная ошибка'}`);
            }
        } catch (error) {
            console.error('Ошибка создания ИБ:', error);
            alert(`❌ Ошибка сети: ${error.message}`);
        } finally {
            setIsCreatingInfobase(false);
        }
    };

    const handleDeleteInfobase = async (ibName) => {
        if (!window.confirm(`Вы уверены, что хотите удалить информационную базу "${ibName}"? Это действие необратимо.`)) {
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/api/infobases/${encodeURIComponent(ibName)}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await response.json();
            if (response.ok && data.success) {
                alert(`✅ ${data.message}`);
                fetchInfoBases();
                fetchLxcContainers();
            } else {
                alert(`❌ Ошибка: ${data.detail || data.message}`);
            }
        } catch (error) {
            console.error('Ошибка удаления ИБ:', error);
            alert(`❌ Ошибка сети: ${error.message}`);
        }
    };
    
    const openGUIStartModal = async () => {
        setGuiStartConfig({ resolution: '1280x1024', infobase_name: '' });
        setSelectedInfobase(null);
        setGuiError(null);
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/infobases`, { 
                headers: { 'Authorization': `Bearer ${token}` } 
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableInfobases(data);
            }
        } catch (error) {
            console.error('Ошибка загрузки списка ИБ:', error);
        }
        
        setShowGUIStartModal(true);
    };
    
    const handleInfobaseSelect = (ibName) => {
        const ib = availableInfobases.find(i => i.name === ibName);
        if (ib) {
            setSelectedInfobase(ib);
            setGuiStartConfig(prev => ({
                ...prev,
                infobase_name: ib.name
            }));
        } else {
            setSelectedInfobase(null);
            setGuiStartConfig(prev => ({
                ...prev,
                infobase_name: ''
            }));
        }
    };

    const startGUISession = async () => {
        if (!token || !selectedInfobase) return;
        
        setIsGUILoading(true);
        setGuiError(null);
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/lxc/${selectedInfobase.container_vmid}/gui/start`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({
                    resolution: guiStartConfig.resolution,
                    infobase_name: selectedInfobase.name
                })
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                setGuiVmid(selectedInfobase.container_vmid);
                setGuiUrl(data.vnc_url);
                setShowGUIStartModal(false);
                setShowGUIModal(true);
            } else {
                setGuiError(data.detail || data.message || 'Неизвестная ошибка');
                alert(`❌ ${data.detail || data.message}`);
            }
        } catch (error) {
            console.error('Ошибка запуска GUI-сессии:', error);
            setGuiError(`Ошибка сети: ${error.message}`);
            alert(`❌ Ошибка сети: ${error.message}`);
        } finally {
            setIsGUILoading(false);
        }
    };
    
    const stopGUISession = async () => {
        if (!token || !guiVmid) return;
        if (!window.confirm('Завершить GUI-сессию? Все несохранённые данные в 1С будут потеряны.')) {
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/api/lxc/${guiVmid}/gui/stop`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setShowGUIModal(false);
                setGuiUrl(null);
                setGuiVmid(null);
            } else {
                alert(`❌ Ошибка: ${data.detail || data.message}`);
            }
        } catch (error) {
            console.error('Ошибка остановки GUI-сессии:', error);
            alert(`❌ Ошибка сети: ${error.message}`);
        }
    };
    
    const navigateToDatabase = (database) => {
        setActiveNav('Базы данных');
        setTimeout(() => { if (database && database.db_name) localStorage.setItem('selectedDatabase', database.db_name); }, 100);
    };
    
    const navigateToContainer = (container) => {
        setActiveNav('Сервера 1C');
        setTimeout(() => { console.log('Переход к контейнеру:', container); }, 100);
    };
    
    const refreshAllData = async () => {
        await fetchLxcContainers();
        await fetchDatabases();
        if (activeNav === 'Информационные базы') {
            await fetchInfoBases();
        }
    };
    
    const handleLaunchLxc = async (e) => {
        e.preventDefault();
        if (!token) return;
        setIsLaunching(true); 
        setLaunchStatus('🚀 Запуск контейнера из готового шаблона 1С...');
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/lxc/launch`, { 
                method: 'POST', 
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
                body: JSON.stringify({ 
                    description: launchConfig.description, 
                    password: launchConfig.password, 
                    ssh_key: launchConfig.sshKey, 
                    dns: launchConfig.dns, 
                    cores: launchConfig.cores, 
                    memory: launchConfig.memory, 
                    disk: launchConfig.disk, 
                    startOnCreate: launchConfig.startOnCreate 
                }) 
            });
            
            if (response.status === 401) { logout(); setIsLaunching(false); return; }
            const data = await response.json();
            
            if (!response.ok || !data.success) { 
                setLaunchStatus(`❌ Ошибка: ${data.detail || data.message || 'Неизвестная ошибка'}`); 
                setIsLaunching(false); 
                return; 
            }
            
            const containerInfo = data.container;
            setLaunchStatus(`✅ Контейнер ${containerInfo.name} (VMID: ${containerInfo.vmid}) успешно запущен!\nIP: ${containerInfo.ip}\nШаблон: готовый образ с 1С (доп. установка не требуется)`);
            
            setTimeout(() => {
                setShowLaunchModal(false); 
                setLaunchStatus('');
                setLaunchConfig({ 
                    description: '', 
                    password: generateRandomPassword(), 
                    sshKey: '', 
                    dns: '8.8.8.8', 
                    cores: 2, 
                    memory: 4096, 
                    disk: 20, 
                    startOnCreate: true 
                });
                setIsLaunching(false); 
                refreshAllData();
            }, 3000);
        } catch (error) { 
            setLaunchStatus(`❌ Ошибка сети: ${error.message}`); 
            setIsLaunching(false); 
        }
    };
    
    const get1cStatusText = (status) => {
        const statusMap = { 'running': 'Запущен', 'stopped': 'Остановлен', 'container_stopped': 'Контейнер выключен', 'not_installed': 'Не установлен', 'error': 'Ошибка', 'timeout': 'Таймаут проверки' };
        return statusMap[status] || status;
    };
    const get1cStatusClass = (status) => {
        const classMap = { 'running': 'running', 'stopped': 'stopped', 'container_stopped': 'container-stopped', 'not_installed': 'not-installed', 'error': 'error', 'timeout': 'timeout' };
        return classMap[status] || 'unknown';
    };

    const InfobasesList = ({ infobases, containerStatus }) => {
        if (!infobases || infobases.length === 0) { 
            return (
                <div className="databases-empty">
                    <small>Нет информационных баз</small>
                </div>
            ); 
        }
        
        return (
            <div className="databases-list-container">
                <div className="databases-header-static">
                    <i className="fas fa-archive" style={{ marginRight: '5px', color: '#667eea' }}></i>
                    <span>Информационные базы ({infobases.length}):</span>
                </div>
                <div className="databases-items-static">
                    {infobases.map((ib, index) => (
                        <div 
                            key={ib.name || index} 
                            className="database-item-static" 
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="db-info" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <i className="fas fa-archive" style={{ color: '#667eea', fontSize: '12px' }}></i>
                                <span style={{ fontSize: '13px', color: '#2c3e50', fontWeight: '500' }}>
                                    {ib.name}
                                </span>
                                <span style={{ 
                                    fontSize: '11px', 
                                    background: '#e9ecef', 
                                    padding: '1px 5px', 
                                    borderRadius: '3px',
                                    color: '#6c757d'
                                }}>
                                    {ib.locale}
                                </span>
                            </div>
                            <div className="db-details" style={{ 
                                display: 'flex', 
                                gap: '8px', 
                                fontSize: '11px', 
                                color: '#666', 
                                marginLeft: '20px', 
                                marginTop: '2px', 
                                flexWrap: 'wrap' 
                            }}>
                                {ib.web_publication_url ? (
                                    <a 
                                        href={ib.web_publication_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ 
                                            color: '#667eea', 
                                            textDecoration: 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '3px'
                                        }}
                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                    >
                                        <i className="fas fa-external-link-alt" style={{ fontSize: '10px' }}></i>
                                        Веб-доступ
                                    </a>
                                ) : (
                                    <span style={{ color: '#999' }}>Не опубликовано</span>
                                )}
                                <span style={{ color: '#28a745' }}>
                                    <i className="fas fa-check-circle" style={{ marginRight: '2px' }}></i>
                                    Активна
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };
    
    const handleContainerAction = async (action, vmid) => {
        if (!token) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/lxc/${vmid}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } });
            if (response.status === 401) { logout(); return; }
            const data = await response.json();
            if (response.ok && data.success) { 
                const actionsMap = { 'start': 'запущен', 'stop': 'остановлен', 'restart': 'перезапущен', 'shutdown': 'выключен' };
                alert(data.message || `Контейнер ${vmid} ${actionsMap[action] || action} успешно`); 
                setTimeout(() => { refreshAllData(); }, 2000); 
            }
            else { alert(`❌ Ошибка: ${data.detail || data.message || 'Неизвестная ошибка'}`); }
        } catch (error) { alert(`❌ Ошибка сети: ${error.message}`); }
    };
    
    const handleNavClick = (navItem) => { setActiveNav(navItem); };
    
    const renderContent = () => {
        if (activeNav === 'Сервера 1C') {
            return (
                <div className="lxc-container">
                    <div className="lxc-header">
                        <h2>LXC Контейнеры на Proxmox с 1С серверами</h2>
                        <button 
                            className="add-lxc-btn launch-lxc-btn" 
                            onClick={() => setShowLaunchModal(true)} 
                            title="Создать контейнер из готового шаблона 1С"
                        >
                            <i className="fas fa-play"></i> Создать новый контейнер с 1С
                        </button>
                    </div>
                    {isLoadingContainers ? (<div className="loading-container"><div className="spinner"></div><p>Загрузка контейнеров из Proxmox...</p></div>) : lxcContainers.length > 0 ? (
                        <div className="lxc-grid">
                            {lxcContainers.map((container) => (
                                <div key={container.vmid} className="card server-card">
                                    <div className="card-status">
                                        <span className={`status-indicator ${container.status}`}>{container.status === 'running' ? '● Запущен' : '○ Остановлен'}</span>
                                        {container.infobases_count > 0 && (
                                            <span 
                                                className="databases-badge" 
                                                title="Количество информационных баз"
                                                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                                            >
                                                <i className="fas fa-archive"></i> {container.infobases_count}
                                            </span>
                                        )}
                                        <span className={`onec-status-indicator ${get1cStatusClass(container.onec_info.service_status)}`}>{container.onec_info.service_status === 'running' ? '●' : container.onec_info.service_status === 'stopped' ? '○' : container.onec_info.service_status === 'not_installed' ? '×' : '?'}</span>
                                    </div>
                                    <h3 className="card-title">{container.name}</h3>
                                    <div className="card-description">
                                        <div className="info-row"><strong>VMID:</strong> {container.vmid}</div>
                                        
                                        <div className="info-row databases-row">
                                            <strong>Информационные базы:</strong>
                                            <InfobasesList infobases={container.infobases} containerStatus={container.status} />
                                        </div>
                                        
                                        <div className="onec-info-section">
                                            <div className="onec-status-row">
                                                <strong>1С</strong>
                                                <div className="onec-status-details">
                                                    {container.onec_info.is_installed ? (<><span className={`onec-badge ${get1cStatusClass(container.onec_info.service_status)}`}>{get1cStatusText(container.onec_info.service_status)}</span>{container.onec_info.version && <span className="onec-version-badge">v{container.onec_info.version}</span>}</>) : (<span className="onec-badge not-installed">Не установлен</span>)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="resources">
                                            <div className="resource-item"><strong>Память:</strong> {Math.round(container.memory / 1024)} GB</div>
                                            <div className="resource-item"><strong>CPU:</strong> {container.cores} ядра</div>
                                            <div className="resource-item"><strong>Диск:</strong> {container.disk} GB</div>
                                        </div>
                                    </div>
                                    <div className="card-meta"><span>Debian 12</span></div>
                                    <div className="card-actions">
                                        <button className="action-btn start-btn" onClick={() => handleContainerAction('start', container.vmid)} disabled={container.status === 'running'}><i className="fas fa-play"></i> Старт</button>
                                        <button className="action-btn stop-btn" onClick={() => handleContainerAction('stop', container.vmid)} disabled={container.status === 'stopped'}><i className="fas fa-stop"></i> Стоп</button>
                                        <button className="action-btn restart-btn" onClick={() => handleContainerAction('restart', container.vmid)} disabled={container.status === 'stopped'}><i className="fas fa-redo"></i> Рестарт</button>
                                        {container.onec_info?.is_installed && container.status === 'running' && (
                                            <button 
                                                className="action-btn" 
                                                style={{ 
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                                onClick={() => openGUIStartModal()}
                                                disabled={isGUILoading}
                                                title="Запустить Конфигуратор 1С"
                                            >
                                                <i className="fas fa-cogs"></i> Конфигуратор
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="full-empty-state">
                            <div className="full-empty-icon">🖥️</div>
                            <div className="full-empty-text">LXC Контейнеры с 1С</div>
                            <div className="full-empty-subtext">{isLoadingContainers ? 'Загрузка данных из Proxmox...' : 'У вас пока нет созданных контейнеров.'}</div>
                            <button className="add-server-btn" onClick={() => setShowLaunchModal(true)}>
                                <i className="fas fa-play"></i> Создать новый контейнер с 1С
                            </button>
                        </div>
                    )}
                    <div className="lxc-footer"><p className="api-info"><small><i className="fas fa-info-circle"></i> Контейнеры создаются из готового шаблона с установленной 1С. Имя генерируется автоматически.</small></p></div>
                </div>
            );
        }
        if (activeNav === 'Базы данных') { return <PostgreSQLManager containers={lxcContainers} onRefreshContainers={refreshAllData} onNavigateToContainer={navigateToContainer} />; }
        
        if (activeNav === 'Информационные базы') {
            return (
                <div className="infobases-container" style={{ padding: '0' }}>
                    <div className="lxc-header">
                        <h2>Информационные базы 1С</h2>
                        <button className="add-lxc-btn" onClick={() => setShowInfobaseModal(true)} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                            <i className="fas fa-plus"></i> Создать информационную базу
                        </button>
                    </div>
                    {isLoadingInfoBases ? (
                        <div className="loading-container">
                            <div className="spinner"></div>
                            <p>Загрузка информационных баз...</p>
                        </div>
                    ) : infoBases.length > 0 ? (
                        <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                                    <tr>
                                        <th style={{ padding: '16px', fontWeight: '600', color: '#2c3e50' }}>Имя ИБ</th>
                                        <th style={{ padding: '16px', fontWeight: '600', color: '#2c3e50' }}>Контейнер</th>
                                        <th style={{ padding: '16px', fontWeight: '600', color: '#2c3e50' }}>Локаль</th>
                                        <th style={{ padding: '16px', fontWeight: '600', color: '#2c3e50' }}>Веб-публикация</th>
                                        <th style={{ padding: '16px', fontWeight: '600', color: '#2c3e50', textAlign: 'right' }}>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {infoBases.map((ib, index) => (
                                        <tr key={ib.name} style={{ borderBottom: '1px solid #e9ecef', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                                            <td style={{ padding: '16px', fontWeight: '500' }}>
                                                <i className="fas fa-database" style={{ color: '#667eea', marginRight: '8px' }}></i>
                                                {ib.name}
                                            </td>
                                            <td style={{ padding: '16px', color: '#6c757d' }}>
                                                {ib.container_name} <span style={{ fontSize: '12px', background: '#e9ecef', padding: '2px 6px', borderRadius: '4px' }}>{ib.container_vmid}</span>
                                            </td>
                                            <td style={{ padding: '16px', color: '#6c757d' }}>{ib.locale}</td>
                                            <td style={{ padding: '16px' }}>
                                                {ib.web_publication_url ? (
                                                    <a 
                                                        href={ib.web_publication_url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        style={{ 
                                                            color: '#667eea', 
                                                            textDecoration: 'none', 
                                                            fontWeight: '500',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '6px'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                    >
                                                        <i className="fas fa-external-link-alt" style={{ fontSize: '12px' }}></i>
                                                        Открыть в браузере
                                                    </a>
                                                ) : (
                                                    <span style={{ color: '#999', fontSize: '13px' }}>Не опубликовано</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '16px', textAlign: 'right' }}>
                                                <button className="action-btn stop-btn" onClick={() => handleDeleteInfobase(ib.name)} style={{ padding: '6px 12px', fontSize: '12px' }}>
                                                    <i className="fas fa-trash"></i> Удалить
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="full-empty-state">
                            <div className="full-empty-icon">🗄️</div>
                            <div className="full-empty-text">Нет информационных баз</div>
                            <div className="full-empty-subtext">Создайте первую информационную базу 1С.</div>
                            <button className="add-server-btn" onClick={() => setShowInfobaseModal(true)} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                <i className="fas fa-plus"></i> Создать информационную базу
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        if (activeNav === 'Резервные копии') { return <div className="backups-container"><h2>Резервные копии</h2><p>Здесь будет отображаться список резервных копий</p></div>; }
        if (activeNav === 'Потребление') { return <div className="consumption-container"><h2>Потребление ресурсов</h2><p>Здесь будет отображаться статистика потребления ресурсов</p></div>; }
        if (activeNav === 'Калькулятор стоимости') { return <div className="calculator-container"><h2>Калькулятор стоимости</h2><p>Здесь будет отображаться калькулятор стоимости облачных услуг</p></div>; }
        
        const cardsData = [
            { id: 1, title: 'Сервера 1C', description: 'Управление LXC контейнерами с 1С серверами', meta1: `${lxcContainers.length} серверов`, meta2: `${lxcContainers.filter(c => c.status === 'running').length} активных`, type: 'onec', icon: 'fa-server', onClick: () => handleNavClick('Сервера 1C') },
            { id: 2, title: 'Базы данных', description: 'Управление базами данных PostgreSQL', meta1: 'Привязаны к контейнерам', meta2: 'Безопасное хранение', type: 'db', icon: 'fa-database', onClick: () => handleNavClick('Базы данных') },
            { id: 3, title: 'Информационные базы', description: 'Централизованное хранилище данных 1С', meta1: `${infoBases.length} баз`, meta2: 'Безопасное хранение', type: 'backup', icon: 'fa-archive', onClick: () => handleNavClick('Информационные базы') },
            { id: 4, title: 'Резервные копии', description: 'Автоматическое создание резервных копий', meta1: 'Ежедневные бекапы', meta2: 'Хранение 30 дней', type: 'backup', icon: 'fa-copy', onClick: () => handleNavClick('Резервные копии') },
            { id: 5, title: 'Потребление', description: 'Мониторинг использования ресурсов платформы 1С.', meta1: 'Текущий месяц', meta2: '85% лимита', type: 'onec', icon: 'fa-chart-line', onClick: () => handleNavClick('Потребление') },
            { id: 6, title: 'Калькулятор стоимости', description: 'Расчет стоимости облачных услуг 1С на основе выбранных ресурсов.', meta1: 'Онлайн расчет', meta2: 'Экспорт отчетов', type: 'project', icon: 'fa-calculator', onClick: () => handleNavClick('Калькулятор стоимости') },
        ];
        
        return (
            <div className="content-grid">
                {cardsData.map(card => (
                    <div key={card.id} className={`card ${card.type}-card clickable`} onClick={card.onClick}>
                        <div className="card-icon"><i className={`fas ${card.icon}`}></i></div>
                        <h3 className="card-title">{card.title}</h3>
                        <p className="card-description">{card.description}</p>
                        <div className="card-meta"><span>{card.meta1}</span><span>{card.meta2}</span></div>
                    </div>
                ))}
            </div>
        );
    };
    
    if (loading) { return <div className="loading-screen"><div className="spinner-large"></div><p>Загрузка приложения...</p></div>; }
    if (!isAuthenticated) { return showLogin ? <Login onRegisterClick={() => setShowLogin(false)} /> : <Register onLoginClick={() => setShowLogin(true)} />; }
    
    return (
        <div className="app">
            <nav className="sidebar">
                <div className="selected-label">1с в LXC</div>
                <div className="nav-section">
                    <a href="#" className={`nav-item ${activeNav === 'Готовое облако 1C' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); handleNavClick('Готовое облако 1С'); }}><i className="fas fa-cloud"></i>Готовое облако 1С</a>
                </div>
                <div className="nav-section">
                    <a href="#" className={`nav-item ${activeNav === 'Сервера 1C' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); handleNavClick('Сервера 1С'); }}><i className="fas fa-server"></i>Сервера 1С{lxcContainers.length > 0 && <span className="nav-badge">{lxcContainers.length}</span>}</a>
                    <a href="#" className={`nav-item ${activeNav === 'Базы данных' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); handleNavClick('Базы данных'); }}><i className="fas fa-database"></i>Базы данных</a>
                    <a href="#" className={`nav-item ${activeNav === 'Информационные базы' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); handleNavClick('Информационные базы'); }}><i className="fas fa-archive"></i>Информационные базы</a>
                    <a href="#" className={`nav-item ${activeNav === 'Резервные копии' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); handleNavClick('Резервные копии'); }}><i className="fas fa-copy"></i>Резервные копии</a>
                </div>
                <div className="nav-section">
                    <a href="#" className={`nav-item ${activeNav === 'Потребление' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); handleNavClick('Потребление'); }}><i className="fas fa-chart-line"></i>Потребление</a>
                    <a href="#" className={`nav-item ${activeNav === 'Калькулятор стоимости' ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); handleNavClick('Калькулятор стоимости'); }}><i className="fas fa-calculator"></i>Калькулятор стоимости</a>
                </div>
                <div className="nav-section user-section">
                    <div className="user-info-compact"><i className="fas fa-user-circle"></i><span className="user-email">{user?.email?.split('@')[0] || 'Пользователь'}</span></div>
                    <div className="user-time-compact"><i className="fas fa-clock"></i><span>{formatTime(accountTime)}</span></div>
                    <button className="nav-item logout-nav-btn" onClick={logout}><i className="fas fa-sign-out-alt"></i>Выйти</button>
                </div>
            </nav>
            <main className="main-content">
                <header className="header">
                    <h1 className="page-title">
                        {activeNav === 'Готовое облако 1С' && <i className="fas fa-cloud"></i>}
                        {activeNav === 'Сервера 1С' && <i className="fas fa-server"></i>}
                        {activeNav === 'Базы данных' && <i className="fas fa-database"></i>}
                        {activeNav === 'Информационные базы' && <i className="fas fa-archive"></i>}
                        {activeNav === 'Резервные копии' && <i className="fas fa-copy"></i>}
                        {activeNav === 'Потребление' && <i className="fas fa-chart-line"></i>}
                        {activeNav === 'Калькулятор стоимости' && <i className="fas fa-calculator"></i>}
                        {activeNav}
                    </h1>
                    <div className="user-info">
                        <div className="user-details">
                            <span className="user-email">{user?.email || 'Пользователь'}</span>
                            <span className="user-time"><i className="fas fa-clock"></i> {formatTime(accountTime)}</span>
                        </div>
                        <button className="logout-btn" onClick={logout} title="Выйти"><i className="fas fa-sign-out-alt"></i></button>
                    </div>
                </header>
                {renderContent()}
            </main>
            
            {showLaunchModal && (
                <div className="modal-overlay" onClick={() => !isLaunching && setShowLaunchModal(false)}>
                    <div className="modal-content lxc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><i className="fas fa-play" style={{ color: '#28a745' }}></i>Создать новый контейнер с 1С</h3>
                            <button className="close-modal-btn" onClick={() => !isLaunching && setShowLaunchModal(false)} disabled={isLaunching}>×</button>
                        </div>
                        
                        <div className="specs-info" style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', marginBottom: '16px' }}>
                            <h4 style={{ color: '#1b5e20', margin: '0 0 8px 0' }}>
                                <i className="fas fa-magic" style={{ marginRight: '8px' }}></i>
                                Автоматическая генерация имени
                            </h4>
                            <p style={{ color: '#1b5e20', margin: 0, fontSize: '13px' }}>
                                Имя контейнера будет сгенерировано автоматически в формате <code>1c-xxxxxx</code> после создания.
                            </p>
                        </div>
                        
                        {launchStatus && (<div className={`creation-status ${launchStatus.includes('❌') ? 'error' : launchStatus.includes('✅') ? 'success' : 'info'}`}><div className="status-message" style={{ whiteSpace: 'pre-line' }}>{launchStatus}</div></div>)}
                        
                        <form onSubmit={handleLaunchLxc}>
                            <div className="template-info-banner">
                                <i className="fas fa-box-open"></i>
                                <div><strong>Готовый шаблон:</strong> <code>1xlcx-85.tar.zst</code><br /><small>Контейнер будет создан из готового образа с уже установленной 1С.</small></div>
                            </div>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Пароль root *</label>
                                    <div className="password-input-group">
                                        <input type="text" value={launchConfig.password} onChange={(e) => setLaunchConfig({...launchConfig, password: e.target.value})} required disabled={isLaunching} />
                                        <button type="button" className="regenerate-password-btn" onClick={() => setLaunchConfig({...launchConfig, password: generateRandomPassword()})} disabled={isLaunching} title="Сгенерировать новый пароль"><i className="fas fa-redo"></i></button>
                                    </div>
                                    <small className="password-hint">Автоматически сгенерированный пароль. Сохраните его!</small>
                                </div>
                                <div className="form-group"><label>DNS сервер</label><input type="text" value={launchConfig.dns} onChange={(e) => setLaunchConfig({...launchConfig, dns: e.target.value})} placeholder="8.8.8.8" disabled={isLaunching} /></div>
                            </div>
                            
                            <div className="form-group"><label>Описание (необязательно)</label><textarea value={launchConfig.description} onChange={(e) => setLaunchConfig({...launchConfig, description: e.target.value})} placeholder="Назначение контейнера" disabled={isLaunching} rows="2" /></div>
                            <div className="form-group"><label>SSH публичный ключ (необязательно)</label><textarea value={launchConfig.sshKey} onChange={(e) => setLaunchConfig({...launchConfig, sshKey: e.target.value})} placeholder="ssh-rsa AAAAB3NzaC1yc2E..." disabled={isLaunching} rows="3" /></div>
                            
                            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#f0f7ff', border: '1px solid #b3d9ff', borderRadius: '8px', marginBottom: '20px' }}>
                                <input type="checkbox" id="startOnCreate" checked={launchConfig.startOnCreate} onChange={(e) => setLaunchConfig({...launchConfig, startOnCreate: e.target.checked})} disabled={isLaunching} style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#28a745' }} />
                                <label htmlFor="startOnCreate" style={{ margin: 0, cursor: 'pointer', fontWeight: '500', color: '#2c3e50', fontSize: '14px' }}>Запустить контейнер сразу после создания</label>
                            </div>
                            
                            <div className="specs-info">
                                <h4>Ресурсы контейнера:</h4>
                                <div className="specs-grid">
                                    <div className="spec-item"><span className="spec-label">CPU (ядер):</span><input type="number" min="1" max="16" value={launchConfig.cores} onChange={(e) => setLaunchConfig({...launchConfig, cores: parseInt(e.target.value) || 2})} disabled={isLaunching} style={{ width: '70px', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>
                                    <div className="spec-item"><span className="spec-label">ОЗУ (МБ):</span><input type="number" min="512" step="512" value={launchConfig.memory} onChange={(e) => setLaunchConfig({...launchConfig, memory: parseInt(e.target.value) || 4096})} disabled={isLaunching} style={{ width: '90px', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>
                                    <div className="spec-item"><span className="spec-label">Диск (ГБ):</span><input type="number" min="5" value={launchConfig.disk} onChange={(e) => setLaunchConfig({...launchConfig, disk: parseInt(e.target.value) || 20})} disabled={isLaunching} style={{ width: '70px', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>
                                    <div className="spec-item"><span className="spec-label">Сеть:</span><span className="spec-value">DHCP (vmbr0)</span></div>
                                    <div className="spec-item"><span className="spec-label">Хранилище:</span><span className="spec-value"><strong>HDD</strong></span></div>
                                    <div className="spec-item"><span className="spec-label">Шаблон:</span><span className="spec-value">1xlcx-85.tar.zst</span></div>
                                </div>
                                <div className="workflow-info">
                                    <p><i className="fas fa-bolt" style={{ color: '#28a745' }}></i> Контейнер будет запущен <strong>мгновенно</strong> из готового шаблона.</p>
                                    <p><i className="fas fa-clock"></i> Время создания: ~30 секунд.</p>
                                </div>
                            </div>
                            
                            <div className="modal-actions">
                                <button type="button" className="cancel-btn" onClick={() => setShowLaunchModal(false)} disabled={isLaunching}>Отмена</button>
                                <button type="submit" className="submit-btn launch-submit-btn" disabled={isLaunching}>{isLaunching ? <><i className="fas fa-spinner fa-spin"></i> Запуск...</> : <><i className="fas fa-play"></i> Запустить контейнер</>}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showInfobaseModal && (
                <div className="modal-overlay" onClick={() => !isCreatingInfobase && setShowInfobaseModal(false)}>
                    <div className="modal-content lxc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><i className="fas fa-database" style={{ color: '#667eea' }}></i>Создать информационную базу 1С</h3>
                            <button className="close-modal-btn" onClick={() => !isCreatingInfobase && setShowInfobaseModal(false)} disabled={isCreatingInfobase}>×</button>
                        </div>
                        <form onSubmit={handleCreateInfobase}>
                            <div className="form-group">
                                <label>Имя информационной базы *</label>
                                <input type="text" value={infobaseConfig.name} onChange={(e) => setInfobaseConfig({...infobaseConfig, name: e.target.value})} placeholder="Например: erp_production" required disabled={isCreatingInfobase} />
                            </div>
                            <div className="form-group">
                                <label>Целевой контейнер *</label>
                                <select value={infobaseConfig.container_vmid} onChange={(e) => setInfobaseConfig({...infobaseConfig, container_vmid: e.target.value})} required disabled={isCreatingInfobase} className="version-select">
                                    <option value="">Выберите запущенный контейнер...</option>
                                    {lxcContainers.filter(c => c.status === 'running').map(container => (
                                        <option key={container.vmid} value={container.vmid}>{container.name} (VMID: {container.vmid})</option>
                                    ))}
                                </select>
                                <small className="password-hint" style={{ color: '#6c757d' }}>База данных PostgreSQL будет автоматически создана и строго изолирована для этого контейнера.</small>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Локаль</label>
                                    <select value={infobaseConfig.locale} onChange={(e) => setInfobaseConfig({...infobaseConfig, locale: e.target.value})} disabled={isCreatingInfobase}>
                                        <option value="ru_RU">ru_RU (Русский)</option>
                                        <option value="en_US">en_US (English)</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Описание (необязательно)</label>
                                    <input type="text" value={infobaseConfig.description} onChange={(e) => setInfobaseConfig({...infobaseConfig, description: e.target.value})} placeholder="Назначение базы" disabled={isCreatingInfobase} />
                                </div>
                            </div>
                            <div className="specs-info" style={{ background: '#f0f7ff', border: '1px solid #b3d9ff' }}>
                                <h4 style={{ color: '#004085' }}><i className="fas fa-shield-alt"></i> Безопасность и изоляция</h4>
                                <ul style={{ fontSize: '13px', color: '#004085', paddingLeft: '20px', margin: '8px 0 0 0' }}>
                                    <li>Для ИБ будет создан <strong>уникальный пользователь PostgreSQL</strong>.</li>
                                    <li>Пользователь имеет права <strong>ТОЛЬКО</strong> на свою базу данных.</li>
                                    <li>Учетные данные генерируются криптографически стойким методом и <strong>никогда не передаются</strong> на фронтенд.</li>
                                </ul>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="cancel-btn" onClick={() => setShowInfobaseModal(false)} disabled={isCreatingInfobase}>Отмена</button>
                                <button type="submit" className="submit-btn" disabled={isCreatingInfobase} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                    {isCreatingInfobase ? <><i className="fas fa-spinner fa-spin"></i> Создание...</> : 'Создать ИБ'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {showGUIStartModal && (
                <div className="modal-overlay" onClick={() => !isGUILoading && setShowGUIStartModal(false)}>
                    <div className="modal-content lxc-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><i className="fas fa-cogs" style={{ color: '#667eea' }}></i>Запуск Конфигуратора 1С</h3>
                            <button className="close-modal-btn" onClick={() => !isGUILoading && setShowGUIStartModal(false)} disabled={isGUILoading}>×</button>
                        </div>
                        
                        <form onSubmit={(e) => { e.preventDefault(); startGUISession(); }}>
                            <div className="specs-info" style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', marginBottom: '16px' }}>
                                <h4 style={{ color: '#1b5e20', margin: '0 0 8px 0' }}>
                                    <i className="fas fa-infinity" style={{ marginRight: '8px' }}></i>
                                    Без ограничений по времени
                                </h4>
                                <p style={{ color: '#1b5e20', margin: 0, fontSize: '13px' }}>
                                    Выберите информационную базу — Конфигуратор автоматически подключится к ней.
                                </p>
                            </div>
                            
                            <div className="form-group">
                                <label>Информационная база *</label>
                                <select 
                                    value={selectedInfobase ? selectedInfobase.name : ''}
                                    onChange={(e) => handleInfobaseSelect(e.target.value)}
                                    disabled={isGUILoading}
                                    className="version-select"
                                >
                                    <option value="">Выберите информационную базу...</option>
                                    {availableInfobases.map(ib => (
                                        <option key={ib.name} value={ib.name}>
                                            {ib.name} (Контейнер: {ib.container_name}, VMID: {ib.container_vmid})
                                        </option>
                                    ))}
                                </select>
                                {availableInfobases.length === 0 && (
                                    <small className="password-hint" style={{ color: '#dc3545' }}>
                                        У вас пока нет информационных баз. Создайте их в разделе "Информационные базы".
                                    </small>
                                )}
                            </div>
                            
                            <div className="form-group">
                                <label>Разрешение экрана</label>
                                <select 
                                    value={guiStartConfig.resolution} 
                                    onChange={(e) => setGuiStartConfig({...guiStartConfig, resolution: e.target.value})}
                                    disabled={isGUILoading}
                                >
                                    <option value="1280x1024">1280×1024 (рекомендуется)</option>
                                    <option value="1920x1080">1920×1080 (Full HD)</option>
                                    <option value="1366x768">1366×768</option>
                                    <option value="1024x768">1024×768</option>
                                </select>
                            </div>
                            
                            <div className="specs-info" style={{ background: '#f0f7ff', border: '1px solid #b3d9ff' }}>
                                <h4 style={{ color: '#004085' }}><i className="fas fa-info-circle"></i> Как это работает</h4>
                                <ul style={{ fontSize: '13px', color: '#004085', paddingLeft: '20px', margin: '8px 0 0 0' }}>
                                    <li>В контейнере запускается виртуальный рабочий стол с Конфигуратором 1С</li>
                                    <li>Конфигуратор автоматически подключится к выбранной информационной базе</li>
                                    <li>Вы подключаетесь через браузер (noVNC) и работаете как с обычным приложением</li>
                                    <li>Мышь, клавиатура и буфер обмена работают полноценно</li>
                                    <li>Сессия активна до вашего явного закрытия</li>
                                </ul>
                            </div>
                            
                            {guiError && (
                                <div className="creation-status error">
                                    <div className="status-message">{guiError}</div>
                                </div>
                            )}
                            
                            <div className="modal-actions">
                                <button type="button" className="cancel-btn" onClick={() => setShowGUIStartModal(false)} disabled={isGUILoading}>Отмена</button>
                                <button 
                                    type="submit" 
                                    className="submit-btn" 
                                    disabled={isGUILoading || !selectedInfobase} 
                                    style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                                >
                                    {isGUILoading ? <><i className="fas fa-spinner fa-spin"></i> Запуск...</> : <><i className="fas fa-play"></i> Запустить Конфигуратор</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {showGUIModal && (
                <div className="modal-overlay" onClick={() => {}}>
                    <div 
                        className="modal-content" 
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                            width: '95vw', 
                            maxWidth: '1600px', 
                            height: '92vh',
                            maxHeight: '1000px',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 0,
                            overflow: 'hidden'
                        }}
                    >
                        <div className="modal-header" style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            padding: '12px 20px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white'
                        }}>
                            <h3 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <i className="fas fa-cogs"></i>
                                Конфигуратор 1С — Контейнер {guiVmid}
                                <span style={{ 
                                    background: 'rgba(40,167,69,0.3)', 
                                    padding: '2px 10px', 
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    marginLeft: '6px',
                                    border: '1px solid rgba(40,167,69,0.5)'
                                }}>
                                    <i className="fas fa-infinity" style={{ marginRight: '4px' }}></i>
                                    Без ограничений
                                </span>
                            </h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <button 
                                    onClick={stopGUISession}
                                    style={{ 
                                        background: 'rgba(220,53,69,0.8)',
                                        border: 'none',
                                        color: 'white',
                                        padding: '8px 16px',
                                        borderRadius: '20px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500'
                                    }}
                                    title="Завершить сессию"
                                >
                                    <i className="fas fa-stop" style={{ marginRight: '6px' }}></i>
                                    Завершить сессию
                                </button>
                            </div>
                        </div>
                        
                        <div style={{ 
                            flex: 1, 
                            background: '#000',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {guiUrl ? (
                                <iframe 
                                    src={guiUrl}
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        border: 'none',
                                        display: 'block'
                                    }}
                                    title="noVNC - Конфигуратор 1С"                                    
                                />
                            ) : (
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    height: '100%',
                                    color: 'white'
                                }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', marginBottom: '20px' }}></i>
                                        <p>Подключение к сессии Конфигуратора...</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div style={{ 
                            padding: '10px 20px',
                            background: '#f8f9fa',
                            borderTop: '1px solid #dee2e6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '13px',
                            color: '#6c757d'
                        }}>
                            <div>
                                <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                                <strong>Подсказки:</strong> 
                                Для полноэкранного режима нажмите <kbd style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>F11</kbd> в браузере. 
                                Буфер обмена синхронизируется автоматически.
                            </div>
                            <div style={{ color: '#28a745', fontWeight: '500' }}>
                                <i className="fas fa-infinity" style={{ marginRight: '4px' }}></i>
                                Сессия активна
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const App = () => { return (<AuthProvider><AppContent /></AuthProvider>); };
export default App;